import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import MainMenu from '../Navigation/MainMenu/MainMenu';
import Countdown from './Countdown/Countdown';
import CampaignMetrics from '../CampaignMetrics/CampaignMetrics';
import HeroBackground from './HeroBackground';
import HeroOverlay from './HeroOverlay';
import styles from './Header.module.scss';
import {
    useNavMenuConfig,
    useKeyVisualConfig,
    useCountdownConfig,
    useMetricsConfig,
    formatCSSValue,
} from '../../store/designStore';

const Header = (props) => {
    const [groupHeights, setGroupHeights] = useState({});

    const onGroupHeightChange = useCallback((id, height) => {
        setGroupHeights((prev) => {
            if (prev[id] === height) return prev;
            const updated = { ...prev };
            if (height === 0) {
                delete updated[id];
            } else {
                updated[id] = height;
            }
            return updated;
        });
    }, []);

    const maxOverlayHeight = useMemo(() => {
        const heights = Object.values(groupHeights);
        if (heights.length === 0) return 0;
        const max = Math.max(...heights);
        return max > 0 ? max + 32 : 0; // 32px padding buffer (16px top/bottom)
    }, [groupHeights]);

    const { isFullWidth: isNavFullWidth, backgroundColor: navBackgroundColor } = useNavMenuConfig();
    const { isFullWidth: isKeyVisualFullWidth } = useKeyVisualConfig();
    const { isFullWidth: isCountdownFullWidth } = useCountdownConfig();
    const {
        detached: { desktop: detachedMetrics, mobile: detachedMetricsMobile } = {},
        offset: { value: metricsOffsetValue, unit: metricsOffsetUnit } = {},
        gap: { desktop: metricsGapValueDesktop, mobile: metricsGapValueMobile } = {},
        borderRadius: detachedMetricsBorderRadius,
    } = useMetricsConfig();

    const isFocusDesktop = Boolean(props.isFocusModeDesktop);
    const isFocusMobile = Boolean(props.isFocusModeMobile);

    const effectiveDetachedMetrics = isFocusDesktop ? false : detachedMetrics || false;
    const effectiveDetachedMetricsMobile = isFocusMobile ? false : detachedMetricsMobile || false;

    const metricsRef = useRef(null);
    const { height: metricsHeight = 90 } = useResizeObserver({
        ref: metricsRef,
        box: 'border-box',
    });

    const getDetachedMarginBottom = (gapValue) => {
        const gapStr = formatCSSValue(gapValue) || '0px';
        const offsetVal = Number(metricsOffsetValue);

        if (isNaN(offsetVal)) {
            return `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapStr}))`;
        }

        if (metricsOffsetUnit === 'px') {
            const netHeight = metricsHeight + offsetVal;
            return `max(0px, calc(${netHeight}px + ${gapStr}))`;
        }

        if (metricsOffsetUnit === '%') {
            const netHeight = metricsHeight * (1 + offsetVal / 100);
            return `max(0px, calc(${netHeight}px + ${gapStr}))`;
        }

        const offsetSign = offsetVal < 0 ? '-' : '+';
        const absOffset = Math.abs(offsetVal);
        return `max(0px, calc(${metricsHeight}px ${offsetSign} ${absOffset}${metricsOffsetUnit} + ${gapStr}))`;
    };

    useEffect(() => {
        const gapDesktopStr = formatCSSValue(metricsGapValueDesktop) || '0px';
        const gapMobileStr = formatCSSValue(metricsGapValueMobile) || '0px';

        if (effectiveDetachedMetrics) {
            document.documentElement.style.setProperty(
                '--metrics-margin-top-desktop',
                `calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapDesktopStr})`
            );
        } else {
            document.documentElement.style.removeProperty('--metrics-margin-top-desktop');
        }

        if (effectiveDetachedMetricsMobile) {
            document.documentElement.style.setProperty(
                '--metrics-margin-top-mobile',
                `calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapMobileStr})`
            );
        } else {
            document.documentElement.style.removeProperty('--metrics-margin-top-mobile');
        }

        return () => {
            document.documentElement.style.removeProperty('--metrics-margin-top-desktop');
            document.documentElement.style.removeProperty('--metrics-margin-top-mobile');
        };
    }, [
        metricsHeight,
        effectiveDetachedMetrics,
        effectiveDetachedMetricsMobile,
        metricsOffsetValue,
        metricsOffsetUnit,
        metricsGapValueDesktop,
        metricsGapValueMobile,
    ]);

    if (isFocusDesktop && isFocusMobile) {
        return (
            <header className="w-100" style={{ display: 'contents' }}>
                <div className={`w-100 sticky-top ${styles.countdownStickyWrapper}`}>
                    <div className={isCountdownFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                        <Countdown />
                    </div>
                </div>
            </header>
        );
    }

    const navFocusClass = isFocusDesktop ? 'd-lg-none' : isFocusMobile ? 'd-none d-lg-block' : '';

    return (
        <header className="w-100" style={{ display: 'contents' }}>
            <div className={`w-100 sticky-top ${styles.countdownStickyWrapper}`}>
                <div className={isCountdownFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                    <Countdown />
                </div>
            </div>

            <div className={navFocusClass || undefined}>
                <div style={navBackgroundColor ? { backgroundColor: navBackgroundColor } : undefined}>
                    <div className={isNavFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                        <MainMenu
                            primaryNavigation={props.primaryNavigation}
                            primaryNavigationCTA={props.primaryNavigationCTA}
                        />
                    </div>
                </div>

                <div
                    className={`position-relative w-100 ${
                        effectiveDetachedMetrics
                            ? `${styles.headerVisualWrapperOverlay} ${effectiveDetachedMetricsMobile ? styles.detachedMobile : ''}`
                            : ''
                    }`}
                    style={
                        effectiveDetachedMetrics
                            ? {
                                  '--metrics-margin-bottom': getDetachedMarginBottom(metricsGapValueDesktop),
                                  ...(effectiveDetachedMetricsMobile
                                      ? {
                                            '--metrics-margin-bottom-mobile':
                                                getDetachedMarginBottom(metricsGapValueMobile),
                                        }
                                      : {}),
                              }
                            : {}
                    }
                >
                    {/* Background Section (Solid Fill vs KeyVisual Image) */}
                    <HeroBackground pageNotFound={props.pageNotFound} maxOverlayHeight={maxOverlayHeight} />

                    {/* Overlaid Elements Grouped by Position */}
                    {!props.pageNotFound && <HeroOverlay onGroupHeightChange={onGroupHeightChange} />}

                    {/* Overlaid Campaign Metrics (Desktop only) */}
                    {!props.pageNotFound && effectiveDetachedMetrics && (
                        <div
                            ref={metricsRef}
                            className={`position-absolute bottom-0 start-50 w-100 ${
                                effectiveDetachedMetricsMobile ? '' : 'd-none d-lg-block'
                            } ${styles.metricsOverlayContainer}`}
                            style={{
                                transform: `translate(-50%, calc(100% + ${metricsOffsetValue}${metricsOffsetUnit}))`,
                            }}
                        >
                            <div className="container-lg">
                                <div
                                    className={styles.metricsOverlayCard}
                                    style={
                                        detachedMetricsBorderRadius !== undefined
                                            ? {
                                                  '--detached-metrics-border-radius':
                                                      formatCSSValue(detachedMetricsBorderRadius),
                                              }
                                            : {}
                                    }
                                >
                                    <CampaignMetrics />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {effectiveDetachedMetrics && (
                    <div
                        className={styles.detachedSpacer}
                        style={{
                            '--spacer-height-desktop': getDetachedMarginBottom(metricsGapValueDesktop),
                            '--spacer-height-mobile': effectiveDetachedMetricsMobile
                                ? getDetachedMarginBottom(metricsGapValueMobile)
                                : '0px',
                        }}
                    />
                )}

                {!effectiveDetachedMetrics || props.pageNotFound ? (
                    <div
                        className={`${isKeyVisualFullWidth ? 'container-fluid px-0' : 'container-lg px-0'} ${
                            styles.inflowMetricsWrapper
                        }`}
                        style={{
                            ...(metricsGapValueDesktop !== undefined
                                ? { '--inflow-margin-bottom-desktop': formatCSSValue(metricsGapValueDesktop) }
                                : {}),
                            ...(metricsGapValueMobile !== undefined
                                ? { '--inflow-margin-bottom-mobile': formatCSSValue(metricsGapValueMobile) }
                                : {}),
                        }}
                    >
                        <CampaignMetrics />
                    </div>
                ) : !effectiveDetachedMetricsMobile ? (
                    <div className="d-lg-none">
                        <div
                            className={`${isKeyVisualFullWidth ? 'container-fluid px-0' : 'container-lg px-0'} ${
                                styles.inflowMetricsWrapper
                            }`}
                            style={{
                                ...(metricsGapValueDesktop !== undefined
                                    ? { '--inflow-margin-bottom-desktop': formatCSSValue(metricsGapValueDesktop) }
                                    : {}),
                                ...(metricsGapValueMobile !== undefined
                                    ? { '--inflow-margin-bottom-mobile': formatCSSValue(metricsGapValueMobile) }
                                    : {}),
                            }}
                        >
                            <CampaignMetrics />
                        </div>
                    </div>
                ) : null}
            </div>
        </header>
    );
};

export default Header;
